// Ported directly from the approved v9 consolidated HTML's own validation functions (EPN-37 Business
// Profile Setup High-Fidelity Reference) so client-side behavior matches the approved prototype exactly.
// The backend performs its own authoritative validation independently — these functions only give the
// Admin immediate feedback and never replace it.

export function validEmail(v: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((v || '').trim());
}

export function validPhone(v: string, required = false): boolean {
  const s = (v || '').trim();
  if (!s) return !required;
  const d = s.replace(/\D/g, '').slice(0, 15);
  return d.length === 10 || (d.length === 11 && d[0] === '1') || (s.startsWith('+') && d.length >= 8 && d.length <= 15);
}

/** Validates the CANONICAL digits-only FEIN value (see shared/formatting.ts's feinDigitsFromInput) —
 * exactly 9 digits, not the "XX-XXXXXXX" DISPLAY format layered on top of it. */
export function validFein(v: string): boolean {
  return /^\d{9}$/.test((v || '').trim());
}

export function validSein(v: string): boolean {
  const s = (v || '').trim();
  return !s || /^[A-Za-z0-9 .#/-]{3,24}$/.test(s);
}

export function validZip(v: string): boolean {
  return /^\d{5}(-\d{4})?$/.test((v || '').trim());
}

export function validWebsite(v: string): boolean {
  const s = (v || '').trim();
  if (!s) return true;
  try {
    const u = new URL(s);
    return /^https?:$/.test(u.protocol) && !!u.hostname && u.hostname.includes('.');
  } catch {
    return false;
  }
}

/** ABA routing-number checksum (the approved v9 prototype's own `routingChecksumValid`). */
export function routingChecksumValid(v: string): boolean {
  const d = (v || '').replace(/\D/g, '').slice(0, 9);
  if (d.length !== 9) return false;
  const n = [...d].map(Number);
  const at = (i: number): number => n[i] ?? 0;
  const sum = 3 * (at(0) + at(3) + at(6)) + 7 * (at(1) + at(4) + at(7)) + (at(2) + at(5) + at(8));
  return sum % 10 === 0;
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  const last = n % 10;
  return n + (last === 1 ? 'st' : last === 2 ? 'nd' : last === 3 ? 'rd' : 'th');
}

export const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
];

/** `end` inclusive day-of-month options, optionally with a trailing "Last Day of Month" (value: "last"). */
export function monthDayOptions(start: number, end: number, includeLast: boolean): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let d = start; d <= end; d++) out.push({ value: String(d), label: ordinal(d) });
  if (includeLast) out.push({ value: 'last', label: 'Last Day of Month' });
  return out;
}

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA',
  'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];
